import React, { useId, useState } from "react";
import { HubCard, FormField, HubInput, HubSelect, ItemList, NameLevelForm, PrimaryBtn, AddSheet, LevelChips, useAddSheet, useEditLayout } from "../editHubPieces";
import { HONOR_LEVELS, IMPACT_LEVELS } from "../editHubModel";
import { FormFieldM } from "../../../../ForBrands/ui/mobile/MobileUI";
import chevron from "../../../../../assets/brand/edit-hub/chevron-14x8.svg";

const listOps = (a, set, key) => ({
  items: a[key] || [],
  onAdd: (item) => set({ [key]: [...(a[key] || []), item] }),
  onRemove: (i) => set({ [key]: (a[key] || []).filter((_, j) => j !== i) }),
});

// Typed inputs (number, date) on phones: @tailwindcss/forms paints
// [type=number] / [type=date] with a grey border after .bm-input, and the
// .bm-phone-form scope does not reset it, so these take the field border
// back (the focus ring still wins).
const PHONE_TYPED = "border-bm-border";

// Half-width fields in a two-up row: when the label and its "Optional" do not
// fit side by side (a 320px phone), "Optional" drops under the label instead
// of the label breaking mid-phrase.
const PAIR = "[&>div:first-child]:flex-wrap [&>div:first-child]:gap-y-0";

// In the add sheets (phones) the date fields carry the select chevron, as on
// the Personal details screen (Figma 2087:13759); the browser's own picker
// button is see-through and sits under it, so a tap on the chevron still
// opens the picker. It is 32px wide so "mm/dd/yyyy" fits a half-width field.
const PHONE_DATE = `${PHONE_TYPED} appearance-none [&::-webkit-date-and-time-value]:text-left [&::-webkit-calendar-picker-indicator]:-mr-[18px] [&::-webkit-calendar-picker-indicator]:w-8 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0`;
const PhoneDate = (props) => (
  <span className="relative block">
    <HubInput type="date" className={PHONE_DATE} {...props} />
    <img src={chevron} alt="" className="pointer-events-none absolute right-[18px] top-1/2 h-2 w-[14px] -translate-y-1/2" />
  </span>
);

// Enrichment. In an add sheet (06b): the field and Add.
const SingleTextForm = ({ label, placeholder, submit }) => {
  const sheet = useAddSheet();
  const id = useId();
  const [v, setV] = useState("");
  const go = () => { if (v.trim()) submit(v.trim()); };
  if (sheet) {
    return (
      <AddSheet onAdd={go} disabled={!v.trim()}>
        <FormFieldM label={label} htmlFor={`${id}-text`}><HubInput id={`${id}-text`} autoFocus value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} /></FormFieldM>
      </AddSheet>
    );
  }
  return (
    <div className="flex items-end gap-3">
      <FormField label={label} className="flex-1"><HubInput autoFocus value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }} /></FormField>
      <PrimaryBtn onClick={go}>Add</PrimaryBtn>
    </div>
  );
};

// v2 · 06 · Academics
export const AcademicsSection = ({ a, set }) => {
  const { phone } = useEditLayout();
  return (
    <>
      <HubCard>
        <FormField label="GPA" optional className="sm:w-[325px]"><HubInput type="number" min={0} max={6} step={0.01} placeholder="3.84" value={a.gpa || ""} onChange={(e) => { const v = parseFloat(e.target.value); set({ gpa: Number.isNaN(v) ? "" : v }); }} {...(phone ? { inputMode: "decimal", className: PHONE_TYPED } : {})} /></FormField>
      </HubCard>
      <ItemList title="Academic achievements" addLabel="Add achievement" {...listOps(a, set, "academicAwards")} render={(it) => ({ label: it.name, level: it.level })} form={({ submit }) => <NameLevelForm levels={IMPACT_LEVELS} placeholder="e.g. National Honor Society" submit={submit} />} />
      <ItemList title="Enrichment" desc="Courses, workshops and certificates." addLabel="Add enrichment" {...listOps(a, set, "enrichments")} render={(it) => ({ label: typeof it === "string" ? it : it.name })} form={({ submit }) => <SingleTextForm label="Course or certificate" placeholder="e.g. Sports Business Certificate, UNLV" submit={submit} />} />
    </>
  );
};

// v2 · 07 · Honors & activities
export const HonorsSection = ({ a, set }) => (
  <>
    <ItemList title="Athletic honors" desc="Awards and recognitions for your play. Pick the level each one was awarded at." addLabel="Add honor" {...listOps(a, set, "athleticAwards")} render={(it) => ({ label: it.name, level: it.level })} form={({ submit }) => <NameLevelForm levels={HONOR_LEVELS} placeholder="e.g. All-State First Team" submit={submit} />} />
    <ItemList title="Activities & leadership" desc="Clubs, volunteering and paid work. Leadership counts toward your IRL score." addLabel="Add activity" {...listOps(a, set, "activities")} render={(it) => ({ label: it.name, level: it.level })} form={({ submit }) => <NameLevelForm levels={IMPACT_LEVELS} placeholder="e.g. Student Council, Vice President" submit={submit} />} />
  </>
);

// In an add sheet (08a): Company, Role, Monthly value | Months two-up, the
// level chips, then Add (web labels and rules, D-e5).
const InternshipForm = ({ submit }) => {
  const sheet = useAddSheet();
  const id = useId();
  const [f, setF] = useState({ companyName: "", role: "", monthlyValue: "", monthsWorked: "", level: "Local" });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.companyName.trim() && f.role.trim() && f.monthsWorked !== "";
  const go = () => { if (!ok) return; submit({ companyName: f.companyName.trim(), role: f.role.trim(), monthlyValue: parseFloat(f.monthlyValue) || 0, monthsWorked: parseInt(f.monthsWorked, 10) || 0, level: f.level }); };
  if (sheet) {
    return (
      <AddSheet onAdd={go} disabled={!ok}>
        <FormFieldM label="Company" htmlFor={`${id}-company`}><HubInput id={`${id}-company`} autoFocus value={f.companyName} placeholder="e.g. Vegas Sports Group" onChange={up("companyName")} /></FormFieldM>
        <FormFieldM label="Role" htmlFor={`${id}-role`}><HubInput id={`${id}-role`} value={f.role} placeholder="e.g. Marketing intern" onChange={up("role")} /></FormFieldM>
        <div className="grid grid-cols-2 items-end gap-3">
          <FormFieldM label="Monthly value ($)" optional htmlFor={`${id}-value`} className={PAIR}><HubInput id={`${id}-value`} type="number" inputMode="decimal" min={0} className={PHONE_TYPED} value={f.monthlyValue} onChange={up("monthlyValue")} /></FormFieldM>
          <FormFieldM label="Months" htmlFor={`${id}-months`} className={PAIR}><HubInput id={`${id}-months`} type="number" inputMode="numeric" min={0} className={PHONE_TYPED} value={f.monthsWorked} onChange={up("monthsWorked")} /></FormFieldM>
        </div>
        <LevelChips levels={IMPACT_LEVELS} value={f.level} onChange={(level) => setF({ ...f, level })} />
      </AddSheet>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Company"><HubInput autoFocus value={f.companyName} placeholder="e.g. Vegas Sports Group" onChange={up("companyName")} /></FormField>
        <FormField label="Role"><HubInput value={f.role} placeholder="e.g. Marketing intern" onChange={up("role")} /></FormField>
      </div>
      <div className="flex items-end gap-3">
        <FormField label="Monthly value ($)" optional className="flex-1"><HubInput type="number" min={0} value={f.monthlyValue} onChange={up("monthlyValue")} /></FormField>
        <FormField label="Months" className="w-[120px]"><HubInput type="number" min={0} value={f.monthsWorked} onChange={up("monthsWorked")} /></FormField>
        <FormField label="Level" className="w-[200px]"><HubSelect value={f.level} onChange={up("level")}>{IMPACT_LEVELS.map((l) => <option key={l}>{l}</option>)}</HubSelect></FormField>
        <PrimaryBtn onClick={go} disabled={!ok}>Add</PrimaryBtn>
      </div>
    </div>
  );
};

// In the "Add a deal" sheet (08b) the web fields, labels and rules stay
// (D-e5): Brand and Deal type are required and come first, then Value, the
// level chips and the two dates two-up. Figma's hint about editing later is
// left out (a deal can only be removed).
const NilForm = ({ submit }) => {
  const sheet = useAddSheet();
  const id = useId();
  const [f, setF] = useState({ title: "", description: "", value: "", startDate: "", endDate: "", level: "Local" });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.title.trim() && f.description.trim();
  const go = () => { if (!ok) return; submit({ title: f.title.trim(), description: f.description.trim(), value: parseFloat(f.value) || 0, startDate: f.startDate, endDate: f.endDate, level: f.level }); };
  if (sheet) {
    return (
      <AddSheet onAdd={go} disabled={!ok}>
        <FormFieldM label="Brand" htmlFor={`${id}-brand`}><HubInput id={`${id}-brand`} autoFocus value={f.title} placeholder="e.g. Cache Athletics" onChange={up("title")} /></FormFieldM>
        <FormFieldM label="Deal type" htmlFor={`${id}-type`}><HubInput id={`${id}-type`} value={f.description} placeholder="e.g. social campaign" onChange={up("description")} /></FormFieldM>
        <FormFieldM label="Value ($)" optional htmlFor={`${id}-value`}><HubInput id={`${id}-value`} type="number" inputMode="decimal" min={0} className={PHONE_TYPED} value={f.value} onChange={up("value")} /></FormFieldM>
        <LevelChips levels={HONOR_LEVELS} value={f.level} onChange={(level) => setF({ ...f, level })} />
        <div className="grid grid-cols-2 items-end gap-3">
          <FormFieldM label="Start date" optional htmlFor={`${id}-start`} className={PAIR}><PhoneDate id={`${id}-start`} value={f.startDate} onChange={up("startDate")} /></FormFieldM>
          <FormFieldM label="End date" optional htmlFor={`${id}-end`} className={PAIR}><PhoneDate id={`${id}-end`} value={f.endDate} onChange={up("endDate")} /></FormFieldM>
        </div>
      </AddSheet>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Brand"><HubInput autoFocus value={f.title} placeholder="e.g. Cache Athletics" onChange={up("title")} /></FormField>
        <FormField label="Deal type"><HubInput value={f.description} placeholder="e.g. social campaign" onChange={up("description")} /></FormField>
        <FormField label="Start date" optional><HubInput type="date" value={f.startDate} onChange={up("startDate")} /></FormField>
        <FormField label="End date" optional><HubInput type="date" value={f.endDate} onChange={up("endDate")} /></FormField>
      </div>
      <div className="flex items-end gap-3">
        <FormField label="Value ($)" optional className="flex-1"><HubInput type="number" min={0} value={f.value} onChange={up("value")} /></FormField>
        <FormField label="Level" className="w-[200px]"><HubSelect value={f.level} onChange={up("level")}>{HONOR_LEVELS.map((l) => <option key={l}>{l}</option>)}</HubSelect></FormField>
        <PrimaryBtn onClick={go} disabled={!ok}>Add</PrimaryBtn>
      </div>
    </div>
  );
};

const money = (v) => (v ? `$${Math.round(Number(v)).toLocaleString()}` : "");

// v2 · 08 · Work & NIL deals
export const WorkSection = ({ a, set }) => (
  <>
    <ItemList title="Internships" desc="Company, role and how long you worked there." addLabel="Add internship" {...listOps(a, set, "internships")} render={(it) => ({ label: [[it.companyName, it.role].filter(Boolean).join(", "), it.monthsWorked ? `${it.monthsWorked} months` : ""].filter(Boolean).join(" · "), level: it.level })} form={({ submit }) => <InternshipForm submit={submit} />} />
    <ItemList title="NIL deals" desc="Deals you have already signed. Optional, but they sharpen your CacheValue™." addLabel="Add deal" sheetTitle="Add a deal" optional {...listOps(a, set, "nilDeals")} render={(it) => ({ label: [[it.title, it.description].filter(Boolean).join(", "), money(it.value)].filter(Boolean).join(" · "), level: it.level })} form={({ submit }) => <NilForm submit={submit} />} />
  </>
);
